import React, { useState, useMemo, useContext } from 'react'
import styled from 'styled-components'
import Muted from '../system/typography/muted'
import FormGroup from '../system/forms/form-group'
import Form from '../system/forms/form'
import FormControl from '../system/forms/form-control'
import Checkbox from '../system/forms/checkbox'
import FormCheck from '../system/forms/form-check'
import breakpoints from '../../styles/breakpoints'
import Fieldset from '../system/forms/fieldset'
import MultiCheck from '../system/forms/multi-check'
import { getData as getCountries } from 'country-list'
import gql from 'graphql-tag'
import { useMutation } from '@apollo/react-hooks'
import {
  ValidatedInput,
  ValidatedTextarea,
  ValidatedSelect,
  ValidatedCheckbox,
  ValidatedPhoneNumber
} from '../system/forms/validated-input'
import ContactFormActions from './contact-form-actions'
import NamePrefixSelect, { NameFormGroup } from './name-prefix-select'
import { EarliestEventContext } from '../../pages/contact/rental'

const CityStateZipFormGroup = styled(FormGroup)`
  grid-template-areas:
    'city city city'
    'state state zip';
  grid-template-columns: repeat(3, 1fr);

  @media (min-width: ${breakpoints.sm}) {
    grid-template-areas: 'city city state state zip';
    grid-template-columns: repeat(5, 1fr);
  }
`

const HandbookLabel = styled.label`
  @media (min-width: ${breakpoints.lg}) {
    max-width: 50%;
  }
`

const SUBMIT_RENTAL_FORM = gql`
  mutation SubmitRentalContactForm($input: RentalContactFormInput!) {
    submitRentalContactForm(input: $input) {
      success
      error
    }
  }
`

const EarliestEventDisclaimer = () => {
  const { earliestEventDaysOffset, earliestEventDate } = useContext(EarliestEventContext)

  return (
    <Muted style={{ marginTop: 0 }} as="p">
      All rental inquiry requests must be submitted at least{' '}
      <strong>{earliestEventDaysOffset} days</strong> prior to the proposed event. Unfortunately, we
      will not be able to consider event proposals that occur before{' '}
      <strong>{earliestEventDate}</strong>.
    </Muted>
  )
}

const RentalForm = () => {
  const requestedLocations = [
    {
      label: 'Allen Performance Hall (1461 Capacity)',
      name: 'PAC'
    },
    {
      label: 'PAC Commons',
      name: 'PAC_COMMONS'
    },
    {
      label: 'Black Box Theatre',
      name: 'BLACK_BOX'
    },
    {
      label: 'Band Hall',
      name: 'BAND_HALL'
    },
    {
      label: 'Choir Room',
      name: 'CHOIR_ROOM'
    },
    {
      label: 'Orchestra Room',
      name: 'ORCHESTRA_ROOM'
    },
    {
      label: 'Dance Studio',
      name: 'DANCE_STUDIO'
    },
    {
      label: 'Multi-Purpose Room',
      name: 'MULTI_PURPOSE_ROOM'
    },
    {
      label: 'Ensemble Room',
      name: 'ENSEMBLE_ROOM'
    },
    {
      label: 'Lowery Freshmen Center Auditorium (850 Capacity)',
      name: 'LFC'
    }
  ]

  const [values, setValues] = useState({
    organizationName: '',
    contactPrefix: '',
    contactFirstName: '',
    contactLastName: '',
    email: '',
    phoneNumber: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    isNonprofit: false,

    eventName: '',
    eventDescription: '',
    numberOfPerformers: '',
    numberOfAttendees: '',
    isTicketed: false,
    isOpenToPublic: false,

    requestedTimes: '',
    requestedLocations: requestedLocations.reduce<{ [key: string]: boolean }>(
      (previous, current) => {
        return {
          ...previous,
          [current.name]: false
        }
      },
      {}
    ),

    handbookAndRates: false
  })

  const handleChange = (event: any) => {
    const target = event.target
    const value = target.type === 'checkbox' ? target.checked : target.value
    const name = target.name

    setValues({
      ...values,
      [name]: value
    })
  }

  const countries = useMemo(() => getCountries().sort((a, b) => a.name.localeCompare(b.name)), [])

  const [submit, { data, loading, error, called }] = useMutation(SUBMIT_RENTAL_FORM)

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const requestedLocations = []
    for (let [key, value] of Object.entries(values.requestedLocations)) {
      if (value) {
        requestedLocations.push(key)
      }
    }

    const data = { ...values, requestedLocations }
    delete data.handbookAndRates

    submit({
      variables: {
        input: data
      }
    })
  }

  const disabled = called && !error

  return (
    <Form onSubmit={handleSubmit}>
      {({ isValid }: { isValid: boolean }) => (
        <>
          <Fieldset
            legendTitle="Organization Info"
            legendDescription="First we'll need a little bit of information about your organization."
            disabled={disabled}
          >
            <FormControl>
              <label htmlFor="organizationName">Organization Name</label>
              <ValidatedInput
                type="text"
                name="organizationName"
                id="organizationName"
                value={values.organizationName}
                onChange={handleChange}
                required
              />
            </FormControl>
            <NameFormGroup>
              <FormControl>
                <label htmlFor="contactPrefix">Prefix</label>
                <NamePrefixSelect
                  name="contactPrefix"
                  id="contactPrefix"
                  value={values.contactPrefix}
                  onChange={handleChange}
                  required
                />
              </FormControl>
              <FormControl>
                <label htmlFor="contactFirstName">Contact First Name</label>
                <ValidatedInput
                  type="text"
                  name="contactFirstName"
                  id="contactFirstName"
                  value={values.contactFirstName}
                  onChange={handleChange}
                  required
                />
              </FormControl>
              <FormControl>
                <label htmlFor="contactLastName">Contact Last Name</label>
                <ValidatedInput
                  type="text"
                  name="contactLastName"
                  id="contactLastName"
                  value={values.contactLastName}
                  onChange={handleChange}
                  required
                />
              </FormControl>
            </NameFormGroup>
            <FormControl>
              <label htmlFor="email">Email</label>
              <ValidatedInput
                type="email"
                name="email"
                id="email"
                value={values.email}
                onChange={handleChange}
                required
              />
            </FormControl>
            <FormControl>
              <label htmlFor="phoneNumber">Phone Number</label>
              <ValidatedPhoneNumber
                name="phoneNumber"
                id="phoneNumber"
                value={values.phoneNumber}
                onValueChange={value => setValues({ ...values, phoneNumber: value.formattedValue })}
                required
              />
            </FormControl>
            <FormControl>
              <label htmlFor="address1">Physical Address</label>
              <ValidatedInput
                type="text"
                name="address1"
                id="address1"
                value={values.address1}
                onChange={handleChange}
                required
              />
            </FormControl>
            <FormControl>
              <label htmlFor="address2">
                Address 2 <Muted>(optional)</Muted>
              </label>
              <ValidatedInput
                type="text"
                name="address2"
                id="address2"
                value={values.address2}
                onChange={handleChange}
              />
            </FormControl>
            <CityStateZipFormGroup>
              <FormControl style={{ gridArea: 'city' }}>
                <label htmlFor="city">City</label>
                <ValidatedInput
                  type="text"
                  name="city"
                  id="city"
                  value={values.city}
                  onChange={handleChange}
                  required
                />
              </FormControl>

              <FormControl style={{ gridArea: 'state' }}>
                <label htmlFor="state">State</label>
                <ValidatedInput
                  type="text"
                  name="state"
                  id="state"
                  value={values.state}
                  onChange={handleChange}
                  required
                />
              </FormControl>

              <FormControl style={{ gridArea: 'zip' }}>
                <label htmlFor="zip">Zip</label>
                <ValidatedInput
                  type="number"
                  name="zip"
                  id="zip"
                  value={values.zip}
                  onChange={handleChange}
                  required
                />
              </FormControl>
            </CityStateZipFormGroup>
            <FormControl>
              <label htmlFor="country">Country</label>
              <ValidatedSelect
                name="country"
                id="country"
                value={values.country}
                onChange={handleChange}
                required
              >
                <option disabled value="">
                  Choose...
                </option>
                <option value="US">United States of America</option>
                <option disabled />
                {countries.map(country => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </ValidatedSelect>
            </FormControl>
            <FormCheck>
              <Checkbox
                name="isNonprofit"
                id="isNonprofit"
                value={values.isNonprofit}
                onChange={handleChange}
              />
              <label htmlFor="isNonprofit">
                My organization is classified as a 501(c)(3) non profit.
              </label>
            </FormCheck>
          </Fieldset>

          <Fieldset
            legendTitle="Event Info"
            legendDescription="Next we'll need a little bit of information about your event."
            disabled={disabled}
          >
            <FormControl>
              <label htmlFor="eventName">Event Name</label>
              <ValidatedInput
                type="text"
                name="eventName"
                id="eventName"
                value={values.eventName}
                onChange={handleChange}
                required
              />
            </FormControl>

            <FormControl>
              <label htmlFor="eventDescription">Event Description</label>
              <ValidatedTextarea
                name="eventDescription"
                id="eventDescription"
                value={values.eventDescription}
                onChange={handleChange}
                required
              />
            </FormControl>

            <FormGroup>
              <FormControl>
                <label htmlFor="numberOfPerformers">Number of Performers</label>
                <ValidatedInput
                  type="text"
                  name="numberOfPerformers"
                  id="numberOfPerformers"
                  value={values.numberOfPerformers}
                  onChange={handleChange}
                  required
                />
              </FormControl>

              <FormControl>
                <label htmlFor="numberOfAttendees">Number of Attendees</label>
                <ValidatedInput
                  type="text"
                  name="numberOfAttendees"
                  id="numberOfAttendees"
                  value={values.numberOfAttendees}
                  onChange={handleChange}
                  required
                />
              </FormControl>
            </FormGroup>

            <FormCheck>
              <Checkbox
                name="isTicketed"
                id="isTicketed"
                value={values.isTicketed}
                onChange={handleChange}
              />
              <label htmlFor="isTicketed">
                Will this event be ticketed?
                <Muted>We provide full box office and ticketing services in house</Muted>
              </label>
            </FormCheck>

            <FormCheck>
              <Checkbox
                name="isOpenToPublic"
                id="isOpenToPublic"
                value={values.isOpenToPublic}
                onChange={handleChange}
              />
              <label htmlFor="isOpenToPublic">Is this event open to the public?</label>
            </FormCheck>
          </Fieldset>

          <Fieldset
            legendTitle="Time &amp; Location"
            legendDescription="When is your event taking place?"
            disabled={disabled}
          >
            <FormControl>
              <label htmlFor="requestedTimes">
                Please list any dates or times requested <EarliestEventDisclaimer />
              </label>
              <ValidatedTextarea
                name="requestedTimes"
                id="requestedTimes"
                value={values.requestedTimes}
                onChange={handleChange}
                required
                placeholder="setup, rehearsals and any other performance times"
              />
            </FormControl>

            <MultiCheck>
              <p>Select all the requested locations</p>
              {requestedLocations.map(requestedLocation => {
                const id = `requestedLocation${requestedLocation.name}`

                return (
                  <FormCheck key={id}>
                    <Checkbox
                      name={id}
                      id={id}
                      value={values.requestedLocations[requestedLocation.name]}
                      onChange={(e: any) => {
                        const newValues = { ...values }
                        newValues.requestedLocations[requestedLocation.name] = e.target.checked
                        setValues(newValues)
                      }}
                    />
                    <label htmlFor={id}>{requestedLocation.label}</label>
                  </FormCheck>
                )
              })}
            </MultiCheck>
          </Fieldset>

          <Fieldset
            legendTitle="Handbook &amp; Rates"
            legendDescription="Please read and review our venue handbook and rates"
            disabled={disabled}
          >
            <FormCheck style={{ alignItems: 'center' }}>
              <ValidatedCheckbox
                name="handbookAndRates"
                id="handbookAndRates"
                value={values.handbookAndRates}
                onChange={handleChange}
                required
              />
              <HandbookLabel htmlFor="handbookAndRates">
                I have reviewed the{' '}
                <a href="/PAC-Handbook.pdf" target="_blank">
                  venue handbook
                </a>{' '}
                and{' '}
                <a href="/PAC-Rental-Rates.pdf" target="_blank">
                  rental rate sheets
                </a>
                .
              </HandbookLabel>
            </FormCheck>
          </Fieldset>

          <ContactFormActions
            isValid={isValid}
            success={data && data.submitRentalContactForm.success}
            loading={loading}
            error={error}
            called={called}
          />
        </>
      )}
    </Form>
  )
}

export default RentalForm
